import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ArrowDown, ArrowUpDown, Plus } from 'lucide-react';
import apiClient from '../../services/api';
import { User, Role } from '../../types/auth';
import { showToast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Select } from '../ui/Select';

type SortField = 'name' | 'role' | 'status';
type SortDir = 'asc' | 'desc';

const UserList = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [pendingAction, setPendingAction] = useState<{ type: 'deactivate' | 'delete'; user: User } | null>(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [search, roleFilter]);

  const fetchRoles = async () => {
    try {
      const response = await apiClient.get('/roles');
      setRoles(response.data.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const params: any = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;

      const response = await apiClient.get('/users', { params });
      setUsers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (user: User) => {
    const action = user.isActive ? 'deactivate' : 'activate';
    try {
      await apiClient.put(`/users/${user.id}`, { isActive: !user.isActive });
      setPendingAction(null);
      showToast(`User ${action}d successfully`, 'success');
      fetchUsers();
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      showToast(`Failed to ${action} user`, 'error');
    }
  };

  const handleDelete = async (user: User) => {
    try {
      await apiClient.delete(`/users/${user.id}`);
      setPendingAction(null);
      showToast('User deleted successfully', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      showToast('Failed to delete user', 'error');
    }
  };

  const getRoleBadgeColor = (roleName: string) => {
    const lower = roleName.toLowerCase();
    if (lower.includes('admin') || lower.includes('super')) return 'bg-purple-100 text-purple-700 border-purple-200';
    if (lower.includes('controller')) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (lower.includes('clearer')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (lower.includes('office')) return 'bg-gray-100 text-gray-700 border-gray-200';
    if (lower.includes('warehouse')) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getUserRoleName = (user: User): string => {
    return typeof user.role === 'string' ? user.role : user.role?.name || '';
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'name':
        return dir * a.name.localeCompare(b.name);
      case 'role':
        return dir * getUserRoleName(a).localeCompare(getUserRoleName(b));
      case 'status': {
        const aVal = a.isActive ? 1 : 0;
        const bVal = b.isActive ? 1 : 0;
        return dir * (aVal - bVal);
      }
      default:
        return 0;
    }
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="inline ml-1 w-3.5 h-3.5 text-slate-300" />;
    return sortDir === 'asc'
      ? <ArrowUp className="inline ml-1 w-3.5 h-3.5 text-blue-500" />
      : <ArrowDown className="inline ml-1 w-3.5 h-3.5 text-blue-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">User Management</h1>
          <p className="text-slate-500 mt-1">Manage system access and permissions</p>
        </div>
        <Link
          to="/admin/users/create"
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add New User
        </Link>
      </div>

      <div className="glass-panel p-4 rounded-xl">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <div className="sm:w-48">
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                  onClick={() => handleSort('name')}
                >
                  User <SortIcon field="name" />
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                  onClick={() => handleSort('role')}
                >
                  Role <SortIcon field="role" />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Skills</th>
                <th
                  className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                  onClick={() => handleSort('status')}
                >
                  Status <SortIcon field="status" />
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {sortedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-sm">
                        {user.name.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-slate-900">{user.name}</div>
                        <div className="text-xs text-slate-500">
                          {(user as any).username && <span className="text-blue-600 font-medium mr-2">@{(user as any).username}</span>}
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRoleBadgeColor(getUserRoleName(user))}`}>
                      {getUserRoleName(user)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.userSkills?.length ? (
                        user.userSkills.map((us) => (
                          <span key={us.skill.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">
                            {us.skill.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-400 italic">No skills</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-3">
                      <Link
                        to={`/admin/users/edit/${user.id}`}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => setPendingAction({ type: 'deactivate', user })}
                        className={`${user.isActive ? 'text-orange-600 hover:text-orange-800' : 'text-green-600 hover:text-green-800'} transition-colors`}
                      >
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => setPendingAction({ type: 'delete', user })}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>

      <ConfirmDialog
          open={!!pendingAction}
          title={pendingAction?.type === 'delete' ? 'Delete User' : `${pendingAction?.user?.isActive ? 'Deactivate' : 'Activate'} User`}
          message={pendingAction?.type === 'delete'
              ? <><strong>Permanently delete "{pendingAction.user.name}"?</strong><br/>This will remove the user and ALL their data. This cannot be undone.</>
              : `Are you sure you want to ${pendingAction?.user?.isActive ? 'deactivate' : 'activate'} ${pendingAction?.user?.name}?`}
          confirmLabel={pendingAction?.type === 'delete' ? 'Delete' : (pendingAction?.user?.isActive ? 'Deactivate' : 'Activate')}
          variant={pendingAction?.type === 'delete' ? 'danger' : 'warning'}
          onConfirm={() => {
              if (!pendingAction) return;
              if (pendingAction.type === 'delete') handleDelete(pendingAction.user);
              else handleDeactivate(pendingAction.user);
          }}
          onCancel={() => setPendingAction(null)}
      />
    </>
  );
};

export default UserList;
